import { useContext, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { CampaignContext } from '../../context/campaign';
import { fetchMyCampaign } from '../../services/campaign';
import { IGrantItem, MyCampaignResponse } from '../../types/campaign';
import { formatCurrency } from '../../services/helper';
import TableContainer from './components/TableContainer';
import { Loading } from '../../shared/components/Loading';
import { CampaignHeaderSection } from '../../shared/components/cards/CampaignItemsCard';
import { ColumnDef } from '@tanstack/react-table';
import { useToasts } from 'react-toast-notifications';
import { GrantsContext } from '~/context/grants';

export const Grants = () => {
  const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
  const [grants] = useContext(GrantsContext);
  const history = useHistory();
  const { addToast } = useToasts();

  function GrantsText() {
    return (
      <div>
        <h1 className='h2'>Grants</h1>
        <p>
          Please note that grants will not be listed here or reflected by your
          fundraising thermometer (if displayed) until they have been processed
          by our office.
        </p>
      </div>
    );
  }

  const data = useMemo(() => {
    if (grants.grants.length) {
      return grants.grants.map((grant: IGrantItem) => {
        return {
          grant_name: grant.grant_name,
          grant_date: grant.grant_date,
          grant_amount: formatCurrency(grant.grant_amount),
          description: grant.description,
        };
      });
    } else {
      return [];
    }
  }, [grants]);

  const columns = useMemo<ColumnDef<IGrantItem>[]>(
    () => [
      {
        id: 'grant',
        header: 'Grant',
        accessorKey: 'grant_name',
        enableSorting: false,
        accessorFn: (props) => {
          return <div className='fw-bold'>{props.grant_name}</div>;
        },
      },
      {
        id: 'date',
        header: 'Date',
        accessorKey: 'grant_date',
        enableSorting: true,
      },
      {
        id: 'amount',
        header: 'Amount',
        accessorKey: 'grant_amount',
        enableSorting: false,
      },
      {
        id: 'message',
        header: 'Message',
        accessorKey: 'description',
        enableSorting: false,
      },
    ],
    []
  );

  useEffect(() => {
    if (!campaign) {
      getMyCampaign();
    }
  }, []);

  const getMyCampaign = async () => {
    try {
      const resp: MyCampaignResponse = await fetchMyCampaign();
      if (resp.detail && resp.detail === 'Invalid token.') {
        // Delete localStorage token
        localStorage.removeItem('token');
        history.push('/login');
      }
      campaignDispatch({
        type: 'set_campaign',
        payload: resp,
      });
    } catch (e) {
      addToast(
        'An error occurred while fetching campaign. Please try again later.',
        { appearance: 'error' }
      );
    }
  };

  if (!campaign) {
    return <Loading />;
  }

  return (
    <div className='container__inner'>
      <CampaignHeaderSection leftcard={<GrantsText />} />

      <TableContainer
        showHeaders
        columns={columns}
        data={data}
        showPaginationComponents={true}
        showFilterComponents={false}
      />
    </div>
  );
};
